import React, { useState, useCallback, useEffect } from "react";
import Select from "../../../../components/form/select/Select";
import Table from "../../../../components/elements/table/Table";
import Input from "../../../../components/form/Input";
import Button from "../../../../components/form/Button";
import { useQuery } from "@tanstack/react-query";
import { ticketList } from "../../../../services/ticketService";
import { toast } from "react-toastify";
import { useSelector } from "react-redux";
import { useNavigate, useOutletContext, useParams } from "react-router-dom";
import { useOrganization } from "../../../../context/OrganizationContext";
import { lngDetect } from "../../../../helpers/lngDetect";
import { t } from "i18next";

const AddAttendees = () => {
  const { lang } = useSelector((state) => state?.langProvider);
  const [selectedOption, setSelectedOption] = useState(null);
  const [selectedItems, setSelectedItems] = useState([]);
  const [ticketRows, setTicketRows] = useState([]);

  const { eventId } = useParams();
  const navigate = useNavigate();
  const { event } = useOutletContext();
  const { selectedOrganization } = useOrganization();

  const [filters, setFilters] = useState({
    limit: 100,
    page: 1,
    sortQuery: "-createdAt",
  });

  const { data: ticket } = useQuery({
    queryKey: ["ticket", filters, selectedOrganization, event?.data?._id],
    queryFn: async () => {
      const res = await ticketList({
        ...filters,
        organization: selectedOrganization,
        event: event?.data?._id,
      });
      if (res?.status !== 200) {
        toast.warning(res?.message);
      }
      return res?.docs;
    },
    enabled: !!event?.data?._id,
  });

  const dropdownData = [
    { name: "Paid with cheque", value: "cheque" },
    { name: "Paid with cash", value: "cash" },
    { name: "Paid directly online with PayPal", value: "PayPal" },
    { name: "Paid online non-PayPal", value: "online" },
    { name: "Complimentary", value: "comp" },
    { name: "No payment necessary", value: "free" },
    { name: "Paid With Voucher", value: "voucher" },
    { name: "Paid Directly By Credit Card", value: "credit" },
    { name: "Paid Directly By Debit Card", value: "debit" },
    { name: "Other", value: "other" },
  ];

  const handleSelectChange = (value) => {
    setSelectedOption(value);
  };

  const tableHeadData = [
    { _id: "name", name: t("Ticket Name"), align: "left" },
    { _id: "ticketType", name: t("Ticket Type"), align: "left" },
    { _id: "sold", name: t("Sold"), align: "center" },
    { _id: "price", name: t("Price"), align: "center" },
    { _id: "quantity", name: t("Quantity"), align: "center" },
    { _id: "faceValue", name: t("Face Value"), align: "center" },
  ];

  useEffect(() => {
    if (ticket && Array.isArray(ticket?.data)) {
      const rows = ticket?.data.map((item) => ({
        _id: item._id,
        rowData: [
          { type: "text", align: "left", data: lngDetect(item?.name) },
          { type: "text", align: "left", data: item?.type },
          {
            type: "text",
            align: "center",
            data: `${item?.quantitySold || 0}/${item?.quantity || 0}`,
          },
          { type: "text", align: "center", data: item?.price || 0 },
          {
            type: item?.quantity ? "input" : "text",
            align: "center",
            data: item?.quantity ? 0 : "N/A",
          },

          {
            type: "text",
            inputPrefix: event?.data?.currency?.symbol || "$",
            align: "center",
            data: 0,
          },
        ],
        ticketItem: item,
      }));
      setTicketRows(rows);
    }
  }, [ticket]);

  const updateCellValue = useCallback((rowId, cellIndex, newValue) => {
    setTicketRows((prevRows) =>
      prevRows.map((row) => {
        if (row._id !== rowId) return row;

        const updatedRowData = row.rowData.map((cell, i) =>
          i === cellIndex ? { ...cell, data: newValue } : cell
        );

        return { ...row, rowData: updatedRowData };
      })
    );
  }, []);

  const tableDataWithHandlers = ticketRows.map((row) => {
    const price = parseFloat(row.rowData[3].data); // index 2 = price
    const ticket_data = ticket?.data?.find((item) => item?._id === row?._id);
    const total_ticket_quentity = ticket_data?.quantity;
    const sold_ticket = ticket_data?.quantitySold;
    const available_ticket_quentity = total_ticket_quentity - sold_ticket;

    return {
      ...row,
      rowData: row.rowData.map((cell, index) => {
        if (index === 4 && cell.type === "input") {
          // Quantity input
          return {
            ...cell,
            cellClass: "w-24",
            onChange: (val) => {
              const input_value = Number(val);

              if (!isNaN(input_value)) {
                const max_val =
                  input_value > available_ticket_quentity
                    ? available_ticket_quentity
                    : input_value;

                updateCellValue(row._id, index, max_val); // Update quantity
                const quantity = parseFloat(max_val);

                if (!isNaN(quantity) && !isNaN(price)) {
                  const calculatedFaceValue = quantity * price;
                  updateCellValue(row._id, 5, calculatedFaceValue); // Override face value
                }
              }
            },
          };
        }

        if (index === 5) {
          return {
            ...cell,
            data: (
              <div className="font-medium">
                {event?.data?.currency?.symbol || "$"} {cell?.data || 0}
              </div>
            ),
          };
        }
        return cell;
      }),
    };
  });

  const selectedTicketsForCheckout = ticketRows
    .map(({ _id, rowData, ticketItem }) => {
      const [ticketName, ticketType, sold, price, qty, totalPrice] =
        rowData.map((r) => r?.data);

      return !isNaN(qty) && qty > 0 && !isNaN(totalPrice)
        ? {
            ...ticketItem,
            id: _id,
            buy_quentity: qty || 0,
            totalPrice: totalPrice || 0,
            originalPrice: price,
          }
        : null;
    })
    .filter(Boolean);

  const handleContinue = () => {
    navigate(`/${lang}/user/checkout/${eventId}`, {
      state: {
        checkoutData: selectedTicketsForCheckout,
        placedBy: "organization",
      },
    });
  };

  return (
    <div className="w-full flex flex-col pb-10 bg-white rounded-md">
      <div className="py-8 px-5 space-y-3">
        <div className="relative">
          <h1 className="font-bold text-lg xl:text-2xl uppercase tracking-tighter font-Montserrat">
            {t("Add Attendees")}
          </h1>
          <div className="text-sm xl:text-base">
            {t(
              "Manually add attendees info for complimentary tickets or offline payments"
            )}
          </div>
        </div>

        <div className="relative">
          <div className="text-sm text-slate-600 mb-2 font-medium">
            * {t("dancebrite does not charge any fees for manual orders")}.
          </div>
          <Table
            tableData={tableDataWithHandlers}
            tableHeadData={tableHeadData}
            isLoder={false}
            pageCount={1}
            currentPage={1}
            setSelectedItems={setSelectedItems}
            selectedItems={selectedItems}
            selectable={false}
          />
        </div>

        <div className="flex justify-end">
          <div className="relative">
            <Input
              label={t("total value")}
              labelClasses={"!text-base capitalize !text-slate-600"}
              inputPrefix={event?.data?.currency?.symbol || "$"}
              inputClasses={"pl-10"}
              inputType="number"
              value={parseFloat(
                ticketRows
                  ?.map((item) => item?.rowData?.at(-1)?.data || 0)
                  ?.reduce((acc, cur) => acc + cur, 0)
              )?.toFixed(2)}
              isDisabled={true}
            />
          </div>
        </div>
        <div className="flex justify-end">
          <div className="relative">
            <Button
              buttonLabel={t("continue")}
              isDisable={!selectedTicketsForCheckout.length}
              buttonFunction={handleContinue}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddAttendees;
